import { Controller } from 'stimulus'
import { loadStripe } from '@stripe/stripe-js/pure'

import { PAYMENTS_LICENSES_URL, STRIPE_PUBLIC_KEY } from '../../_js/base/endpoints'
import { isEmail } from '../../_js/base/utils'
import { getCurrencyController } from '../../_js/controllers/currency-controller'

import { fetchCheckoutInfo, fetchClientSecret, recreateCheckout } from './store-controller-utils'

export default class StoreController extends Controller {
  static targets = [
    'buyerEmail',
    'customerEmail',
    'submitButton',
    'step1',
    'heading',
    'subHeading',
    'serial',
    'invoiceNumber',
    'navStep2',
    'checkbox',
    'navigation',
    'error',
    'errorRefresh',
  ]

  static classes = ['error', 'loading', 'active']

  checkout = null

  checkoutId = null

  stripe = null

  static steps = {
    step1: {},
    step2: {},
  }

  currentStep = StoreController.steps.step1

  async getOrCreateStripe() {
    if (this.stripe) {
      return this.stripe
    }

    this.stripe = await loadStripe(STRIPE_PUBLIC_KEY)
    return this.stripe
  }

  async createOrUpdateCheckout(getClientSecret) {
    if (this.checkout) {
      this.checkout.destroy()
    }

    this.element.classList.add(this.loadingClass)

    try {
      const stripe = await this.getOrCreateStripe()
      const { secret, id } = await getClientSecret()

      this.checkout = await stripe.initEmbeddedCheckout({ fetchClientSecret: () => secret })
      this.checkout.mount('#checkout')
      this.checkoutId = id
    } catch (e) {
      this.renderErrorState()
    } finally {
      this.element.classList.remove(this.loadingClass)
      this.element.classList.add(this.activeClass)
    }
  }

  async resumePreviousSession(secret, id) {
    await this.createOrUpdateCheckout(async () => {
      const { currency } = await fetchCheckoutInfo(id)
      this.forceSetCurrency(currency)

      this.currentStep = StoreController.steps.step2
      this.updateNavContent()

      return { secret, id }
    })
  }

  async purchaseSession(customerEmail, buyerEmail) {
    await this.createOrUpdateCheckout(async () => {
      const url = `${PAYMENTS_LICENSES_URL}/1/checkout/session/new_license`

      if (!buyerEmail) {
        buyerEmail = customerEmail
        customerEmail = null
      }

      this.currentStep = StoreController.steps.step2
      this.updateNavContent()

      const body = { email: buyerEmail, on_behalf_of: customerEmail, currency: this.selectedCurrency }
      const { secret, id } = await fetchClientSecret(url, body)

      return { secret, id }
    })
  }

  async addSeatsSession(serial, licenseRef, seats, buyer, currency) {
    await this.createOrUpdateCheckout(async () => {
      const url = `${PAYMENTS_LICENSES_URL}/1/checkout/session/add_seats`
      const bodyObject = { seats: seats, currency }

      if (serial) {
        bodyObject.serial = serial
      }

      if (licenseRef) {
        bodyObject.license_ref = licenseRef
      }

      if (buyer) {
        bodyObject.buyer = buyer
      }

      this.currentStep = StoreController.steps.step2
      this.updateNavContent()

      const { secret, id } = await fetchClientSecret(url, bodyObject)

      return { secret, id }
    })
  }

  async renewSession(serial, licenseRef, seats, buyer, currency) {
    await this.createOrUpdateCheckout(async () => {
      const url = `${PAYMENTS_LICENSES_URL}/1/checkout/session/renew_license`

      const body = { seats: seats, currency }

      if (serial) {
        body.serial = serial
      }

      if (licenseRef) {
        body.license_ref = licenseRef
      }

      if (buyer) {
        body.buyer = buyer
      }

      this.currentStep = StoreController.steps.step2
      this.updateNavContent()

      const { secret, id } = await fetchClientSecret(url, body)

      return { secret, id }
    })
  }

  updateNavContent() {
    if (this.currentStep === StoreController.steps.step1) {
      this.step1.classList.remove('is-hidden')
    } else {
      this.step1.classList.add('is-hidden')
      this.navStep2Target.classList.add('store__list-item--is-active')
    }
  }

  async connect() {
    const urlParams = new URLSearchParams(window.location.search)

    this.currentStep = StoreController.steps.step1
    this.updateNavContent()

    const license = urlParams.get('license')
    const licenseID = urlParams.get('licenseID')
    const seats = urlParams.get('seats')
    const operation = urlParams.get('operation')
    const buyer = urlParams.get('buyer')

    const session = urlParams.get('session')
    const id = urlParams.get('id')
    const currency = urlParams.get('currency')

    if (session) {
      await this.resumePreviousSession(session, id)
      return
    }

    if (currency) {
      this.forceSetCurrency(currency)
    }

    if (license || licenseID) {
      switch (operation) {
        case 'add-seats':
          await this.addSeatsSession(license, licenseID, parseInt(seats), buyer, currency)
          return
        case 'renew':
          await this.renewSession(license, licenseID, parseInt(seats), buyer, currency)
          return

        default:
          break
      }

      return
    }

    // we're not resuming a previous session or going directly to an add/renew session,
    // so we need to show the email form
    this.step1.classList.remove('is-hidden')

    // and we want the email input ready for the user to type
    this.customerEmail.focus()

    this.validate()
  }

  async checkboxChange(event) {
    const { target } = event
    const { checked } = target

    if (checked) {
      this.buyerEmail.style.display = 'block'
    } else {
      this.buyerEmail.style.display = 'none'
    }

    this.validate()
  }

  async click(event) {
    event.preventDefault()

    if (this.submitButton.disabled) {
      return
    }

    await this.purchaseSession(this.customerEmail.value, this.buyerEmail.value)
  }

  validate() {
    this.submitButton.disabled = !isEmail(this.customerEmail.value) || !this.selectedCurrency

    if (this.checkboxTarget.checked) {
      this.submitButton.disabled = this.submitButton.disabled || !isEmail(this.buyerEmail.value)
      this.buyerEmail.style.display = 'block'
    } else {
      this.buyerEmail.style.display = 'none'
    }
  }

  get buyerEmail() {
    return this.buyerEmailTarget
  }

  get submitButton() {
    return this.submitButtonTarget
  }

  get step1() {
    return this.step1Target
  }

  get heading() {
    return this.headingTarget
  }

  get subHeading() {
    return this.subHeadingTarget
  }

  get customerEmail() {
    return this.customerEmailTarget
  }

  setCurrency(e) {
    const { currency } = e.detail

    if (this.selectedCurrency === currency) {
      return
    }

    this.selectedCurrency = currency

    if (this.checkout) {
      this.createOrUpdateCheckout(async () => {
        const { secret, id } = await recreateCheckout(this.checkoutId, currency)
        return { secret, id }
      })
    }
  }

  forceSetCurrency(currency) {
    const currencyController = getCurrencyController(this.application)
    currencyController.setCurrency(currency)
  }

  renderErrorState() {
    this.navigationTarget?.classList.add('store__navigation--hidden')
    this.step1Target?.classList.add('store__step--hidden')
    this.errorTarget?.classList.remove('store-error-state--hidden')
  }

  refresh() {
    window.location.reload()
  }
}
